<template>
  <!-- Drawer content -->
  <SmDrawer
    v-model:visibility="localVisibility"
    :title="i18n.t('optimizations')"
    class="!w-[clamp(700px,45vw,600px)]">
    <template #close>
      <SmTooltip :content="i18n.t('reloadOptimizations')">
        <RefreshCcw
          :size="18"
          class="cursor-pointer text-gray-500 transition hover:scale-110 hover:text-contrast dark:text-gray-300"
          @click="refetchOptimizations" />
      </SmTooltip>

      <SmButtonClose @close="localVisibility = !localVisibility" />
    </template>
    <!-- Drawer Content -->
    <template #default>
      <!-- Header -->
      <el-tabs v-model="tabsFilter" class="optimizationFilterTabs">
        <el-tab-pane name="open" :label="i18n.t('detected')" />
        <el-tab-pane name="hidden" :label="i18n.t('hidden')" />
        <el-tab-pane name="deprecated" :label="i18n.t('completed')" />
      </el-tabs>

      <div class="flex items-center gap-3 pb-4">
        <div class="w-80">
          <SmInput
            v-model="searchQuery"
            outline
            size="medium"
            :label="i18n.t('search')">
          </SmInput>
        </div>

        <!-- Filter -->
        <SmDropdown
          :close-on-click-inside="false"
          dropdown-class="w-[30rem] max-w-7xl">
          <template #trigger>
            <SmButton outline size="small">
              <div class="gap flex items-center gap-2">
                <Filter :size="18" />
                <p>
                  <span>
                    {{ i18n.t('filter') }}
                  </span>
                  <span v-if="filterCount"> ({{ filterCount }}) </span>
                </p>
              </div>
            </SmButton>
          </template>
          <div style="padding: 1rem">
            <OptimizationFilterDialog
              :filter="activeFilter"
              :applications="applications"
              @update:filter="(val) => setFilter(val)" />
          </div>
        </SmDropdown>

        <div class="flex-1" />

        <SmTooltip :content="i18n.t('download')">
          <SmButton size="small" @click="exportDialogVisible = true">
            <Download class="text-white" :size="18" />
            <span class="ml-2">{{ i18n.t('export') }}</span>
          </SmButton>
        </SmTooltip>
      </div>
      <!-- Table -->
      <OptimizationTable
        v-if="optimizations && applications"
        :filtered-count="optimizationCount"
        :total-count="optimizationTotalCount"
        :optimizations="optimizations"
        :applications="applications"
        @scroll-end-reached="loadMoreOptimizations" />

      <OptimizationExportDialog v-model:visibility="exportDialogVisible" />
    </template>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { Download, Filter, RefreshCcw } from 'lucide-vue-next'
  import { SoftwareOut } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import {
    EnrichedOptimizations,
    OptimizationStore,
  } from '@/stores/optimization/optimization.store'
  import { useI18n } from 'vue-i18n'
  import { OptimizationFilters } from '@/stores/optimization/optimization.filter'
  import { refetchOptimizations } from '@/utils/optimizations'
  import OptimizationExportDialog from './OptimizationExportDialog.vue'

  const i18n = useI18n()

  const props = defineProps({
    drawerVisibility: {
      type: Boolean,
      required: true,
    },
    initalFilter: {
      type: Object as PropType<OptimizationFilters>,
      required: false,
      default() {
        return { hidden: false, deprecated: false, hideExpenseless: true }
      },
    },
  })

  const emit = defineEmits(['update:drawerVisibility'])

  const localVisibility = computed({
    get: () => props.drawerVisibility,
    set: (value) => emit('update:drawerVisibility', value),
  })

  const exportDialogVisible = ref<boolean>(false)

  // ----------------------------------------------------------------------
  // Load Data
  // ----------------------------------------------------------------------

  const applications = ref<SoftwareOut[]>([])

  onMounted(() => {
    loadApplications()
  })

  function loadApplications() {
    ApplicationStore.getApplications<SoftwareOut>().then((res) => {
      applications.value = res
    })
  }

  // ----------------------------------------------------------------------
  // Handle Filter
  // ----------------------------------------------------------------------

  const tabsFilter = ref<string>('open')
  const searchQuery = ref<string>('')
  const activeFilter = ref<OptimizationFilters>({})
  const filterCount = computed(() => {
    const filter = Object.keys(activeFilter.value).filter((key) => {
      const filterKey = key as keyof OptimizationFilters
      return (
        filterKey !== 'hidden' &&
        filterKey !== 'deprecated' &&
        activeFilter.value[filterKey] !== undefined &&
        activeFilter.value[filterKey] !== false
      )
    })
    return filter.length
  })

  // mount filters
  watch(
    () => props.initalFilter,
    (newFilter) => {
      setFilter(newFilter)
    },
    { immediate: true }
  )

  //unmount filters
  watch(localVisibility, (visible) => {
    if (!visible) {
      setFilter({
        application_ids: undefined,
        type: undefined,
        search: undefined,
      })
      searchQuery.value = ''
      tabsFilter.value = 'open'
    }
  })

  function setFilter(newFilter: OptimizationFilters) {
    activeFilter.value = {
      ...activeFilter.value,
      ...newFilter,
    }
  }

  watch(searchQuery, () => {
    if (searchQuery.value) {
      setFilter({ search: searchQuery.value })
    } else {
      setFilter({ search: undefined })
    }
  })

  watch(tabsFilter, () => {
    switch (tabsFilter.value) {
      case 'open':
        setFilter({ hidden: false, deprecated: false })
        break
      case 'hidden':
        setFilter({ hidden: true, deprecated: false })
        break
      case 'deprecated':
        setFilter({ hidden: false, deprecated: true, hideExpenseless: false })
        break
      default:
        break
    }
  })

  watch(activeFilter, () => {
    updateParameters(activeFilter.value)
    updateCountParameters(activeFilter.value)
    updateTotalCountParameters(activeFilter.value)
  })

  // ----------------------------------------------------------------------
  // Handle Table Data
  // ----------------------------------------------------------------------

  const INITIAL_ROW_LIMIT = 300
  const ROW_EXPAND_COUNT = 100

  async function loadMoreOptimizations() {
    updateParameters(
      {},
      { limit: optimizations.value.length + ROW_EXPAND_COUNT }
    )
  }

  const { data: optimizations, updateParameters } =
    OptimizationStore.useOptimizations<EnrichedOptimizations>(
      activeFilter.value,
      {
        limit: INITIAL_ROW_LIMIT,
      },
      undefined,
      true
    )

  const { count: optimizationCount, updateParameters: updateCountParameters } =
    OptimizationStore.useOptimizationsCount(activeFilter.value, true)

  const {
    count: optimizationTotalCount,
    updateParameters: updateTotalCountParameters,
  } = OptimizationStore.useOptimizationsCount({
    hidden: activeFilter.value.hidden,
    deprecated: activeFilter.value.deprecated,
  })
</script>

<style lang="scss">
  .optimizationFilterTabs {
    .el-tabs__item {
      padding: 0 10px !important;
    }
  }
</style>
